import { useState, type ReactNode } from "react";
import {
  ClipboardList,
  ChevronDown,
  FileClock,
  Info,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  PackageSearch,
  PanelLeftClose,
  PanelLeftOpen,
  Warehouse,
  Phone,
  Settings2,
  UsersRound,
} from "lucide-react";
import { Modal } from "./ui";
import type { CurrentUser } from "../types";
import { api } from "../api";
import { Button, FormField } from "./ui";

export type Route =
  | "overview"
  | "boms"
  | "calculations"
  | "inventory"
  | "catalogs"
  | "history"
  | "admin";

const navigation: Array<{
  id: Route;
  label: string;
  icon: typeof LayoutDashboard;
}> = [
  { id: "overview", label: "Tổng quan", icon: LayoutDashboard },
  { id: "calculations", label: "Danh sách phiếu tính", icon: PackageSearch },
  { id: "history", label: "Lịch sử hoạt động", icon: FileClock },
  { id: "boms", label: "Định mức vật tư", icon: ClipboardList },
  { id: "inventory", label: "Kho & tồn kho", icon: Warehouse },
  { id: "catalogs", label: "Danh mục", icon: UsersRound },
  { id: "admin", label: "Quản trị hệ thống", icon: Settings2 },
];

const navigationGroups = [
  { label: "QUẢN LÝ TÍNH TOÁN", routes: ["overview", "calculations", "history"] as Route[] },
  { label: "ĐỊNH MỨC VÀ KHO", routes: ["boms", "inventory"] as Route[] },
  { label: "DANH MỤC & HỆ THỐNG", routes: ["catalogs", "admin"] as Route[] },
];

const roleLabels: Record<string, string> = {
  superadmin: "Quản trị hệ thống",
  bom_editor: "Biên soạn BOM",
  bom_approver: "Phê duyệt BOM",
  viewer: "Chỉ xem",
};

export function AppShell({
  route,
  onNavigate,
  user,
  onLogout,
  children,
}: {
  route: Route;
  onNavigate(route: Route): void;
  user: CurrentUser;
  onLogout(): void;
  children: ReactNode;
}) {
  const [supportOpen, setSupportOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="app-shell workspace-shell">
      <div className="workspace-body">
        <aside className={["app-sidebar", sidebarOpen ? "open" : "", sidebarCollapsed ? "collapsed" : ""].filter(Boolean).join(" ")}>
          <div className="sidebar-brand">
            <a
              className="sidebar-brand-link"
              href="#overview"
              onClick={(event) => {
                event.preventDefault();
                onNavigate("overview");
                setSidebarOpen(false);
              }}
            >
              <img src="/assets/vinh-hoan-logo.png?v=20260810" alt="Logo doanh nghiệp" />
              <span>
                <strong>BOM System</strong>
                <small>Quản lý định mức vật tư</small>
              </span>
            </a>
            <button className="sidebar-collapse" type="button" onClick={() => setSidebarCollapsed((value) => !value)} title={sidebarCollapsed ? "Mở rộng điều hướng" : "Thu gọn điều hướng"} aria-label={sidebarCollapsed ? "Mở rộng điều hướng" : "Thu gọn điều hướng"}>
              {sidebarCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          </div>
          <nav className="sidebar-nav" aria-label="Điều hướng chính">
            {navigationGroups.map((group) => (
              <section className="sidebar-nav-group" key={group.label}>
                <h2>{group.label}</h2>
                {group.routes.map((routeId) => {
                  const item = navigation.find((entry) => entry.id === routeId)!;
                  return <button
                    type="button"
                    key={item.id}
                    className={route === item.id ? "nav-active" : ""}
                    onClick={() => { onNavigate(item.id); setSidebarOpen(false); }}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <item.icon size={19} aria-hidden="true" /> <span>{item.label}</span>
                  </button>;
                })}
              </section>
            ))}
          </nav>
        </aside>
        <div className="app-stage">
          <header className="topbar workspace-topbar">
            <button className="sidebar-toggle" type="button" onClick={() => setSidebarOpen((value) => !value)} aria-label="Mở điều hướng"><Menu size={21} /></button>
            <div className="workspace-context" aria-hidden="true" />
            <div className="utility workspace-utility">
              <div className="account">
                <button
                  className="account-profile"
                  type="button"
                  onClick={() => setAccountOpen(true)}
                  title="Tài khoản của tôi"
                >
                  <span>{user.displayName.slice(0, 2).toUpperCase()}</span>
                  <div>
                    <strong>{user.displayName}</strong>
                    <small>{user.authMethod === "local" ? "Đổi mật khẩu" : "Quản lý tài khoản Microsoft"}</small>
                  </div>
                  <ChevronDown className="account-profile-caret" size={15} aria-hidden="true" />
                </button>
                <button onClick={onLogout} title="Đăng xuất" aria-label="Đăng xuất">
                  <LogOut size={17} />
                </button>
              </div>
            </div>
          </header>
          <main>{children}</main>
          <footer className="app-footer workspace-footer">
            <em className="workspace-copyright">© 2026 - Phòng Kế hoạch. All rights reserved.</em>
            <button
              className="app-footer-support"
              type="button"
              onClick={() => setSupportOpen(true)}
              aria-label="Thông tin hỗ trợ"
              title="Thông tin hỗ trợ"
            >
              <Info size={17} aria-hidden="true" />
            </button>
          </footer>
        </div>
      </div>
      <Modal
        open={supportOpen}
        title="Thông tin hỗ trợ"
        onClose={() => setSupportOpen(false)}
      >
        <div className="support-modal-content">
          <Info className="support-modal-icon" size={20} aria-hidden="true" />
          <div className="support-modal-contact">
            <strong>Trần Châu Trùng Dương</strong>
            <span>Phòng Hành chính - Nhân sự</span>
          </div>
          <a href="mailto:duong.tran@vinhhoan.com">
            <Mail size={17} aria-hidden="true" />
            duong.tran@vinhhoan.com
          </a>
          <span className="support-modal-zalo">
            <Phone size={17} aria-hidden="true" />
            Zalo: 0888240490
          </span>
          <p>Liên hệ khi cần hỗ trợ sử dụng hệ thống.</p>
        </div>
      </Modal>
      {user.authMethod === "local" ? <ChangePasswordModal open={accountOpen} user={user} onClose={() => setAccountOpen(false)} /> : <MicrosoftAccountModal open={accountOpen} user={user} onClose={() => setAccountOpen(false)} />}
    </div>
  );
}

function MicrosoftAccountModal({ open, user, onClose }: { open: boolean; user: CurrentUser; onClose(): void }) {
  return <Modal open={open} title="Tài khoản của tôi" onClose={onClose}>
    <div className="modal-form account-modal">
      <FormField label="Họ tên"><input readOnly value={user.displayName} /></FormField>
      <FormField label="Email"><input readOnly value={user.email} /></FormField>
      <p className="muted">Bạn đang đăng nhập bằng Microsoft. Mật khẩu và thông tin bảo mật được quản lý bởi Microsoft, nên không đổi mật khẩu trong BOM System.</p>
      <div className="modal-actions">
        <Button type="button" variant="outline" onClick={onClose}>Đóng</Button>
        <a className="button primary" href="https://myaccount.microsoft.com/" target="_blank" rel="noreferrer">Quản lý tài khoản Microsoft</a>
      </div>
    </div>
  </Modal>;
}
/* obsolete shell removed
          <a
            className="workspace-brand-link"
            href="#overview"
            onClick={(event) => {
              event.preventDefault();
              onNavigate("overview");
              setSidebarOpen(false);
            }}
          >
            <img src="/assets/vinh-hoan-logo.png?v=20260810" alt="Logo doanh nghiệp" />
            <span>
              <strong>BOM System</strong>
              <small>Quản lý định mức vật tư</small>
            </span>
          </a>
        </div>
        <nav className="workspace-tabs" aria-label="Điều hướng chính">
          {primaryNavigation.map((item) => (
            <button
              type="button"
              key={item.id}
              className={route === item.id ? "nav-active" : ""}
              onClick={() => onNavigate(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="utility workspace-utility">
          <button className="topbar-help" type="button" onClick={() => setSupportOpen(true)} title="Hỗ trợ sử dụng" aria-label="Hỗ trợ sử dụng"><Info size={20} /></button>
          <div className="account">
            <button
              className="account-profile"
              type="button"
              onClick={() => setAccountOpen(true)}
              title="Tài khoản của tôi"
            >
              <span>{user.displayName.slice(0, 2).toUpperCase()}</span>
              <div>
                <strong>{user.displayName}</strong>
                <small>{roleLabels[user.roles[0] ?? ""] ?? "Người dùng"}</small>
              </div>
            </button>
            <button onClick={onLogout} title="Đăng xuất">
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </header>
      <div className="workspace-body">
        <aside className={["app-sidebar", sidebarOpen ? "open" : "", sidebarCollapsed ? "collapsed" : ""].filter(Boolean).join(" ")}>
          <nav className="sidebar-nav" aria-label="Điều hướng chi tiết">
            {navigationGroups.map((group) => (
              <section className="sidebar-nav-group" key={group.label}>
                <h2>{group.label}</h2>
                {group.routes.map((routeId) => {
                  const item = navigation.find((entry) => entry.id === routeId)!;
                  return <button
                    type="button"
                    key={item.id}
                    className={route === item.id ? "nav-active" : ""}
                    onClick={() => { onNavigate(item.id); setSidebarOpen(false); }}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <item.icon size={19} aria-hidden="true" /> <span>{item.label}</span>
                  </button>;
                })}
              </section>
            ))}
          </nav>
          <button className="sidebar-collapse" type="button" onClick={() => setSidebarCollapsed((value) => !value)} title={sidebarCollapsed ? "Mở rộng điều hướng" : "Thu gọn điều hướng"}>
            {sidebarCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}<span>{sidebarCollapsed ? "Mở rộng" : "Thu gọn"}</span>
          </button>
        </aside>
        <div className="app-stage">
        <main>{children}</main>
        <footer className="app-footer workspace-footer">
          <span className="workspace-copyright">© 2026 · Phòng Kế hoạch</span>
          <button
            className="app-footer-support"
            type="button"
            onClick={() => setSupportOpen(true)}
            aria-label="Thông tin hỗ trợ"
            title="Thông tin hỗ trợ"
          >
            <Info size={17} aria-hidden="true" />
          </button>
        </footer>
        </div>
      </div>
      <Modal
        open={supportOpen}
        title="Thông tin hỗ trợ"
        onClose={() => setSupportOpen(false)}
      >
        <div className="support-modal-content">
          <Info className="support-modal-icon" size={20} aria-hidden="true" />
          <div className="support-modal-contact">
            <strong>Trần Châu Trùng Dương</strong>
            <span>Phòng Hành chính - Nhân sự</span>
          </div>
          <a href="mailto:duong.tran@vinhhoan.com">
            <Mail size={17} aria-hidden="true" />
            duong.tran@vinhhoan.com
          </a>
          <span className="support-modal-zalo">
            <Phone size={17} aria-hidden="true" />
            Zalo: 0888240490
          </span>
          <p>Liên hệ khi cần hỗ trợ sử dụng hệ thống.</p>
        </div>
      </Modal>
      <ChangePasswordModal
        open={accountOpen}
        user={user}
        onClose={() => setAccountOpen(false)}
      />
    </div>
  );
}
*/

function ChangePasswordModal({
  open,
  user,
  onClose,
}: {
  open: boolean;
  user: CurrentUser;
  onClose(): void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage("Xác nhận mật khẩu mới chưa khớp.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await api("/me/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage("Đã đổi mật khẩu đăng nhập nội bộ.");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Không thể đổi mật khẩu.");
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal open={open} title="Tài khoản của tôi" onClose={onClose}>
      <form className="modal-form" onSubmit={save}>
        <p className="muted">{user.email}</p>
        <FormField label="Mật khẩu hiện tại">
          <input required type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
        </FormField>
        <FormField label="Mật khẩu mới">
          <input required type="password" minLength={8} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
        </FormField>
        <FormField label="Nhập lại mật khẩu mới">
          <input required type="password" minLength={8} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
        </FormField>
        {message && <p className={message.startsWith("Đã") ? "form-message" : "form-error"}>{message}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Đóng</Button>
          <Button type="submit" disabled={saving}>Đổi mật khẩu</Button>
        </div>
      </form>
    </Modal>
  );
}

export function PageHeader({
  title,
  actions,
}: {
  title: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      <div>{actions}</div>
    </div>
  );
}
